import React from 'react'
import styled from '@emotion/styled'
import { mediaQueriesByDevice } from 'parameters/index'
import { LoadingBoundary } from 'components/patterns/suspense/Suspense'
import { DomainThemeProvider } from 'components/patterns/themes/context/ThemeProvider'
import { SquareTile } from './SquareTile'
import { MorePurposesIcon } from 'experiences/explore/loans/components/icons'
import { ThemedIcon } from 'components/patterns/themes/components/ThemedIcon'
import { FlexGrower, Title } from 'experiences/explore/loans/components/NewLoanSearchWidget/styles'
import { PendingSearchSummary, SearchSummary } from '../../../../search/widgets/SearchSummary'
import {
	ctaConfigs,
	PendingSearchProps,
	useSearchDatawrapperState
} from 'experiences/explore/loans/components/NewLoanSearchWidget/config'
import { css } from '@emotion/react'
import { LoanPurposeList } from 'experiences/explore/loans/components/NewLoanSearchWidget/LoanPurposeList'
import { useLatestSearch } from 'services/FionaAPI/Search/hooks'
import { BlockComponent } from 'lib'
import { SuspenseNullState } from 'components/patterns/suspense/errors'
import { cnfSearchRedirectLink } from 'experiences/explore/loans/components/NewLoanSearchWidget/config'
import { UrlObject } from 'url'

const ConditionalRenderer: React.FC<React.PropsWithChildren<PendingSearchProps>> = ({ data }) => {
	try {
		const searchData = data.read()
		if (searchData) {
			return <SearchSummary search={searchData} />
		} else {
			return <LoanPurposeList />
		}
	} catch (e) {
		if (e instanceof SuspenseNullState) {
			return <LoanPurposeList />
		} else {
			throw e
		}
	}
}

export const NewLoanSearchWidget: BlockComponent = () => {
	const data = useLatestSearch()
	const mode = useSearchDatawrapperState(data)
	const config = ctaConfigs[mode]

	const url = new URL(cnfSearchRedirectLink.search)
	const destinationUrl: UrlObject = {
		protocol: url.protocol,
		host: url.host,
		hostname: url.hostname,
		pathname: url.pathname
	}

	return (
		<SearchWrapper>
			<Title>{config.sectionTitle}</Title>
			<SearchDataRow>
				<FlexGrower>
					<LoadingBoundary fallback={mode === 'loading' ? <PendingSearchSummary /> : <LoanPurposeList />}>
						<ConditionalRenderer data={data} />
					</LoadingBoundary>
				</FlexGrower>
				<DomainThemeProvider theme={config.themeOverride}>
					<SquareTile text={config.tileTitle} destination={destinationUrl}>
						<ThemedIcon Icon={MorePurposesIcon} />
					</SquareTile>
				</DomainThemeProvider>
			</SearchDataRow>
		</SearchWrapper>
	)
}

const constants = css`
	--search-row-height: 168px;

	${mediaQueriesByDevice.tablet} {
		--search-row-height: 400px;
	}
`

const SearchWrapper = styled.div`
	${constants};
	position: absolute;
	left: 0;
	bottom: -70px;
	height: var(--search-row-height);
	width: 100%;

	${mediaQueriesByDevice.tablet} {
		bottom: calc(var(--search-row-height) - 80);
	}
`

const SearchDataRow = styled.div`
	display: flex;
	flex-wrap: nowrap;
	width: 100%;
	-webkit-overflow-scrolling: touch;
	justify-content: space-between;
	padding: 0 16px;
	overflow-x: scroll;
	overflow-y: visible;

	&::-webkit-scrollbar {
		display: none;
	}

	${mediaQueriesByDevice.tablet} {
		flex-direction: column;
		overflow: visible;
	}
`
